import { ActionButton, FeedItem, NetworkStatus, isRequestInFlight } from '@knocklabs/client';
import { useKnockFeed, useNotificationStore } from '@knocklabs/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import {
  getNotificationActionUrl,
  openNotificationActionUrl,
} from '../shared/notification-action-url';
import {
  NotificationFeedListItemModel,
  mapNotificationFeedItem,
} from './item/map-notification-feed-item';
import { NotificationFeedFilter } from './list/notification-feed-filter';
import { useNotificationsPopoverClose } from './useNotificationsPopoverClose';

const fallbackTitleMessage = defineMessage({
  id: 'notifications.item.fallbackTitle',
  defaultMessage: 'Notificación',
});

/**
 * Knock feed data + actions for the custom MUI notifications list.
 */
export function useNotificationsFeedList() {
  const { feedClient } = useKnockFeed();
  const closePopover = useNotificationsPopoverClose();
  const items = useNotificationStore(feedClient, (state) => state.items);
  const unreadCount = useNotificationStore(feedClient, (state) => state.metadata.unread_count);
  const pageInfo = useNotificationStore(feedClient, (state) => state.pageInfo);
  const networkStatus = useNotificationStore(feedClient, (state) => state.networkStatus);
  const { formatMessage } = useIntl();
  const fallbackTitle = formatMessage(fallbackTitleMessage);

  const [filter, setFilter] = useState<NotificationFeedFilter>('all');

  useEffect(() => {
    void feedClient.fetch({ status: filter });
  }, [feedClient, filter]);

  const listItems: NotificationFeedListItemModel[] = useMemo(
    () => items.map((item) => mapNotificationFeedItem(item, fallbackTitle, formatMessage)),
    [items, fallbackTitle, formatMessage],
  );

  const findItem = useCallback(
    (id: string) => items.find((entry) => entry.id === id) as FeedItem | undefined,
    [items],
  );

  const navigateAndClose = useCallback(
    (url: string) => {
      openNotificationActionUrl(url);
      closePopover?.();
    },
    [closePopover],
  );

  /**
   * Primary navigation only: mark interacted + open when the item has an action URL.
   * Rows without a URL are not clicked to mark as read (use archive / mark-all instead).
   */
  const handleItemClick = useCallback(
    (id: string) => {
      const item = findItem(id);
      if (!item) {
        return;
      }

      const actionUrl = getNotificationActionUrl(item);
      if (!actionUrl) {
        return;
      }

      void feedClient.markAsInteracted(item, {
        type: 'cell_click',
        action: actionUrl,
      });
      navigateAndClose(actionUrl);
    },
    [feedClient, findItem, navigateAndClose],
  );

  const handleArchive = useCallback(
    (id: string) => {
      const item = findItem(id);
      if (!item) {
        return;
      }

      void feedClient.markAsArchived(item);
    },
    [feedClient, findItem],
  );

  const handleButtonClick = useCallback(
    (id: string, button: ActionButton) => {
      const item = findItem(id);
      if (!item) {
        return;
      }

      void feedClient.markAsInteracted(item, {
        type: 'button_click',
        name: button.name,
        label: button.label,
        action: button.action,
      });

      if (button.action) {
        navigateAndClose(button.action);
      }
    },
    [feedClient, findItem, navigateAndClose],
  );

  const handleMarkAllAsRead = useCallback(() => {
    void feedClient.markAllAsRead();
  }, [feedClient]);

  const handleLoadMore = useCallback(() => {
    void feedClient.fetchNextPage();
  }, [feedClient]);

  return {
    items: listItems,
    unreadCount,
    filter,
    setFilter,
    hasMore: Boolean(pageInfo.after),
    isLoading: networkStatus === NetworkStatus.loading,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    isRequestInFlight: isRequestInFlight(networkStatus),
    onItemClick: handleItemClick,
    onArchive: handleArchive,
    onButtonClick: handleButtonClick,
    onMarkAllAsRead: handleMarkAllAsRead,
    onLoadMore: handleLoadMore,
  };
}
